import type { Session } from '@supabase/supabase-js'
import { createContext, ReactElement, ReactNode, useContext, useEffect, useState } from 'react'
import { onAuthStateChange } from './auth'
import { fetchProfile, Profile } from './profile'

/**
 * The four things every screen downstream of auth needs to know, and nothing more.
 *
 * Deliberately does not include a `GateState` — see `gate.ts`'s `decideGate`, which is called
 * by `AppRoutes` (Task 12) using these values, not by this module. This file is a "what do we
 * know" data layer; the branching logic lives elsewhere.
 */
export interface SessionContextValue {
  session: Session | null
  profile: Profile | null
  loading: boolean
  refresh: () => Promise<void>
}

const SessionContext = createContext<SessionContextValue | undefined>(undefined)

/**
 * Holds the one `supabase.auth` subscription this whole app uses, via `onAuthStateChange`
 * (`./auth`, Task 6). No other component subscribes to auth state directly — everything
 * downstream reads through `useSession()`.
 *
 * On mount, subscribes once. Supabase fires an `INITIAL_SESSION` event on every fresh
 * subscription carrying whatever session currently exists (or `null`), which is the only way
 * this component learns the starting session — it never calls `supabase.auth.getSession()`
 * separately, and never imports `./supabase` at all.
 *
 * On every callback (including that first `INITIAL_SESSION` one): `session` is set to whatever
 * came back. If it's non-null, the profile is fetched and `profile` set to whatever
 * `fetchProfile()` returns (`null` on a genuine RLS zero-row result or a fetch error — this
 * file does not distinguish the two, matching `gate.ts`'s `NO_PROFILE` collapse). If it's
 * `null` (signed out), `profile` is set to `null` immediately with no fetch. Either way,
 * `loading` becomes `false` once this first resolves — the only place it ever does.
 *
 * `refresh()` never touches `loading`; see its own doc comment below.
 */
export function SessionProvider({ children }: { children: ReactNode }): ReactElement {
  const [session, setSession] = useState<Session | null>(null)
  const [profile, setProfile] = useState<Profile | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const {
      data: { subscription },
    } = onAuthStateChange((_event, newSession) => {
      setSession(newSession)
      if (newSession === null) {
        setProfile(null)
        setLoading(false)
        return
      }
      fetchProfile(newSession.user.id)
        .then(({ profile: fetchedProfile }) => {
          setProfile(fetchedProfile)
        })
        .finally(() => {
          setLoading(false)
        })
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [])

  /**
   * Re-fetches the profile on demand, for a caller that needs to pick up a server-side change
   * it caused itself — e.g. `JoinScreen` after `redeem_invite` flips `status` from PENDING to
   * ACTIVE, or a retry button on the `NO_PROFILE` screen.
   *
   * Never touches `loading`: `loading` represents only "has the app resolved its initial auth
   * state yet," and flipping it back to `true` on every mid-session refresh would flash the
   * whole app back to its initial-load screen instead of updating in place. Callers that want
   * their own "refreshing…" affordance render it themselves.
   *
   * A no-op that resolves immediately when there is no current session — nothing to refresh,
   * and RLS would return zero rows for the fetch anyway.
   */
  async function refresh(): Promise<void> {
    if (session === null) return
    const { profile: fetchedProfile } = await fetchProfile(session.user.id)
    setProfile(fetchedProfile)
  }

  return (
    <SessionContext.Provider value={{ session, profile, loading, refresh }}>
      {children}
    </SessionContext.Provider>
  )
}

/**
 * Reads the current auth/profile state from the nearest `SessionProvider`.
 *
 * @throws If called outside a `SessionProvider` — a missing provider is a bug, not a state to
 *         render around silently.
 */
export function useSession(): SessionContextValue {
  const context = useContext(SessionContext)
  if (context === undefined) {
    throw new Error('useSession() must be called within a SessionProvider')
  }
  return context
}
